import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Input from '@Components/Input';
import SimpleSpinner from '@Components/Results/utils/SimpleSpinner';
import Button from '@Components/Button';
import { Checkbox } from '@els/els-react--checkbox';
import { validateName, NAME_ERRORS } from '@Utils/Name';
import { EMAIL_ERRORS, validateEmailWithErrors } from '@Utils/Email';
import { PASSWORD_ERRORS, validateConfirmationPassword, validatePassword } from '@Utils/Password';
import { trackRegistrationErrors } from '../analytics';

import './RegisterFormContent.custom.scss';
import './RegisterFormContent.theme.scss';

const nameMaxLength = 50;
const nameMinLength = 2;
const firstNameErrorsMessages = {
  [NAME_ERRORS.REQUIRED]: 'First name is required.',
  [NAME_ERRORS.MIN_LENGTH]: `First name must have minimum of ${nameMinLength} characters.`,
  [NAME_ERRORS.MAX_LENGTH]: `First name must have maximum of ${nameMaxLength} characters.`,
  [NAME_ERRORS.CHECK_ASCII]: `First name must contain only ASCII characters.`,
};

const lastNameErrorsMessages = {
  [NAME_ERRORS.REQUIRED]: 'Last name is required.',
  [NAME_ERRORS.MIN_LENGTH]: `Last name must have minimum of ${nameMinLength} characters.`,
  [NAME_ERRORS.MAX_LENGTH]: `Last name must have maximum of ${nameMaxLength} characters.`,
  [NAME_ERRORS.CHECK_ASCII]: `Last name must contain only ASCII characters.`,
};

const emailErrorsMessages = {
  [EMAIL_ERRORS.REQUIRED]: 'Email address is required.',
  [EMAIL_ERRORS.CHECK_EMAIL]: `Please provide a valid email address.`,
};

const passwordErrorsMessages = {
  [PASSWORD_ERRORS.REQUIRED]: 'Password is required.',
  [PASSWORD_ERRORS.LOWER_CASE]: 'Password must have at least one lower case character.',
  [PASSWORD_ERRORS.UPPER_CASE]: 'Password must have at least one upper case character.',
  [PASSWORD_ERRORS.DIGIT_OR_SPECIAL]: 'Password must have at least one numeric character or special characters.',
  [PASSWORD_ERRORS.MIN_LENGTH]: 'Password must have minimum of 8 characters.',
  [PASSWORD_ERRORS.MAX_LENGTH]: 'Password must have maximum of 200 characters.',
  [PASSWORD_ERRORS.RESTRICT]: 'Password must not contain prohibited expression.',
  [PASSWORD_ERRORS.SEQUENCE]: 'Password must not contain sequential alpha numeric characters.',
  [PASSWORD_ERRORS.CHECK_ASCII]: 'Password must contain only printable, non-whitespace ASCII characters.',
  [PASSWORD_ERRORS.COMPARE_TO]: 'Password does not match. Please try again.',
  [PASSWORD_ERRORS.CONFIRM_PASSWORD_REQUIRED]: 'Confirm password is required.',
};

const AGREEMENT_ERROR_MESSAGE = 'Registered user agreement is required';

function RegisterFormContent({ registerFail, isLoading, submit, data, setData, isShibboleth, isUsernameVisible }) {
  const spinnerId = `registerForm-${new Date().valueOf()}`;

  const [touchedFields, setTouchedFields] = useState([]);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const isFieldTouched = (field) => {
    return touchedFields.find((touchedField) => touchedField === field);
  };

  const touchField = (field) => {
    if (isFieldTouched(field)) {
      return;
    }
    setTouchedFields([...touchedFields, field]);
  };

  const resolveValidationMessage = (fieldName, errors, errorMessages, textOnly = false) => {
    if (!isFieldTouched(fieldName) && !isFormSubmitted && !textOnly) {
      return null;
    }

    if (!errors || !errors.length) {
      return null;
    }

    return errors.map((error) => {
      return textOnly ? errorMessages[error] : <div key={errorMessages[error]}>{errorMessages[error]}</div>;
    });
  };

  const validateFirstNameField = () => {
    const { errors } = validateName(data.firstName, nameMaxLength, nameMinLength);
    return errors;
  };

  const resolveFirstNameValidationMessage = (textOnly) => {
    // eslint-disable-next-line consistent-return
    return resolveValidationMessage('firstName', validateFirstNameField(), firstNameErrorsMessages, textOnly);
  };

  const validateLastNameField = () => {
    const { errors } = validateName(data.lastName, nameMaxLength, nameMinLength);
    return errors;
  };

  const resolveLastNameValidationMessage = (textOnly) => {
    // eslint-disable-next-line consistent-return
    return resolveValidationMessage('lastName', validateLastNameField(), lastNameErrorsMessages, textOnly);
  };

  const validateEmailField = () => {
    const { errors } = validateEmailWithErrors(data.email);
    return errors;
  };

  const resolveEmailValidationMessage = (textOnly) => {
    // eslint-disable-next-line consistent-return
    return resolveValidationMessage('email', validateEmailField(), emailErrorsMessages, textOnly);
  };

  const validatePasswordField = () => {
    const { errors } = validatePassword(data.password);
    return errors;
  };

  const resolvePasswordValidationMessage = (textOnly) => {
    if (isShibboleth) {
      return null;
    }
    // eslint-disable-next-line consistent-return
    return resolveValidationMessage('password', validatePasswordField(), passwordErrorsMessages, textOnly);
  };

  const validateRepeatPasswordField = () => {
    const { errors } = validateConfirmationPassword(data.password, data.repeatPassword);
    return errors;
  };

  const resolveRepeatPasswordValidationMessage = (textOnly) => {
    if (isShibboleth) {
      return null;
    }
    // eslint-disable-next-line consistent-return
    return resolveValidationMessage('repeatPassword', validateRepeatPasswordField(), passwordErrorsMessages, textOnly);
  };

  const changeFirstName = (value) => {
    setData({
      firstName: value,
    });
  };

  const changeLastName = (value) => {
    setData({
      lastName: value,
    });
  };

  const changeEmail = (value) => {
    setData({
      email: value,
    });
  };

  const changePassword = (value) => {
    setData({
      password: value,
    });
  };

  const changeRepeatPassword = (value) => {
    setData({
      repeatPassword: value,
    });
  };

  const changeRememberMe = (e) => {
    setData({
      rememberMe: e.target.checked,
    });
  };

  const changeMarketingInfo = (e) => {
    setData({
      marketingInfo: e.target.checked,
    });
  };

  const changeLicenseAgreement = (e) => {
    setData({
      licenseAgreement: e.target.checked,
    });
  };

  const submitHandler = () => {
    if (!isFormSubmitted) {
      setIsFormSubmitted(true);
    }

    const errors = [
      ...(resolveFirstNameValidationMessage(true) || []),
      ...(resolveLastNameValidationMessage(true) || []),
      ...(resolveEmailValidationMessage(true) || []),
      ...(resolvePasswordValidationMessage(true) || []),
      ...(resolveRepeatPasswordValidationMessage(true) || []),
    ];

    if (!data.licenseAgreement) {
      errors.push(AGREEMENT_ERROR_MESSAGE);
    }

    if (errors && errors.length) {
      trackRegistrationErrors(errors, true);
      return;
    }
    submit();
  };

  return (
    <>
      <form name="registerForm">
        <div className="login-form__content register-form__content">
          {isLoading && <SimpleSpinner id={spinnerId} />}
          <div className="register-form__content_input_wrapper">
            <div className="els-text-input">
              <Input
                clearButton={false}
                id="register-first-name"
                name="firstName"
                classNameInput="e2e-register-first-name"
                animatedPlaceholder
                placeholder="First Name"
                value={data.firstName}
                onChange={changeFirstName}
                maxLength={50}
                minLength={2}
                onMouseDown={() => touchField('firstName')}
                validationMessage={resolveFirstNameValidationMessage()}
                required
                debounceDelay={0}
                disabled={isLoading}
              />
            </div>

            <div className="els-text-input">
              <Input
                clearButton={false}
                id="register-last-name"
                name="lastName"
                classNameInput="e2e-register-last-name"
                animatedPlaceholder
                placeholder="Last Name"
                value={data.lastName}
                onChange={changeLastName}
                onMouseDown={() => touchField('lastName')}
                validationMessage={resolveLastNameValidationMessage()}
                required
                debounceDelay={0}
                disabled={isLoading}
              />
            </div>
          </div>
          {isUsernameVisible && (
            <div className="els-text-input register-form__content_placeholder">
              <Input
                clearButton={false}
                id="register-username"
                name="username"
                classNameInput="e2e-register-username"
                animatedPlaceholder
                placeholder="Username"
                value={data.userName}
                disabled
              />
            </div>
          )}
          <div className="els-text-input">
            <Input
              clearButton={false}
              id="register-email"
              name="email"
              classNameInput="e2e-register-email"
              animatedPlaceholder
              placeholder="Email address"
              value={data.email}
              onChange={changeEmail}
              onMouseDown={() => touchField('email')}
              validationMessage={resolveEmailValidationMessage()}
              required
              debounceDelay={0}
              disabled={isLoading}
            />
          </div>

          {!isShibboleth && (
            <div className="els-text-input">
              <Input
                clearButton={false}
                id="register-password"
                name="password"
                classNameInput="e2e-register-password"
                animatedPlaceholder
                placeholder="Password"
                value={data.password}
                onChange={changePassword}
                onMouseDown={() => touchField('password')}
                validationMessage={resolvePasswordValidationMessage()}
                required
                debounceDelay={0}
                disabled={isLoading}
                type="password"
              />
            </div>
          )}

          {!isShibboleth && (
            <div className="els-text-input">
              <Input
                clearButton={false}
                id="register-confirm-password"
                name="repeatPassword"
                classNameInput="e2e-register-confirm-password"
                animatedPlaceholder
                placeholder="Confirm password"
                value={data.repeatPassword}
                onChange={changeRepeatPassword}
                onMouseDown={() => touchField('repeatPassword')}
                validationMessage={resolveRepeatPasswordValidationMessage()}
                required
                debounceDelay={0}
                disabled={isLoading}
                type="password"
              />
            </div>
          )}

          <div className="els-text-input els-checkbox-input">
            <div className="register-form__content_checkbox">
              <Checkbox
                onChange={changeRememberMe}
                checked={data.rememberMe}
                disabled={isLoading}
                tabIndex={0}
                id="register_remember"
                ariaControlsGuidance="Remember me on this computer .Do not use on a shared computer!"
              />
              <div className="login-form__content_remember-text">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="register_remember" title="Remember me on this computer">
                  <span>Remember me on this computer</span>
                </label>
                <span>(Do not use on a shared computer!)</span>
              </div>
            </div>
          </div>
          <div className="els-text-input els-checkbox-input">
            <div className="register-form__content_checkbox">
              <Checkbox
                checked={data.marketingInfo}
                disabled={isLoading}
                id="register_marketingInfo"
                tabIndex={0}
                onChange={changeMarketingInfo}
                ariaControlsGuidance="Stay informed about Elsevier products and services"
              />
              <div className="login-form__content_remember-text">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="register_marketingInfo" title="Stay informed">
                  <span>Stay informed about Elsevier products and services</span>
                </label>
              </div>
            </div>
          </div>
          <div className="els-text-input els-checkbox-input">
            <div className="register-form__content_checkbox">
              <Checkbox
                checked={data.licenseAgreement}
                disabled={isLoading}
                id="register_licenseAgreement"
                tabIndex={0}
                onChange={changeLicenseAgreement}
                ariaControlsGuidance="I have read and agree to the"
              />
              <div className="register-form__content_checkbox">
                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                <label htmlFor="register_licenseAgreement" title="I agree to the Registered User Agreement">
                  <span>
                    I have read and agree to the
                    <a
                      className="blue-link"
                      target="_blank"
                      href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
                      rel="noreferrer">
                      Registered User Agreement
                    </a>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {isFormSubmitted && !data.licenseAgreement && (
            <div className="login-form__content_error">
              Please read and then check the Registered User Agreement. Then click Register again.
            </div>
          )}

          {registerFail && <div className="login-form__content_error">{registerFail}</div>}

          <div className="login-form__content_actions">
            <div className="login-form__content_actions-button">
              <Button
                buttonConfig={{
                  type: 'primary',
                  size: 'md',
                  label: 'Register',
                  e2eName: 'login-sign-in',
                }}
                iconConfig={{
                  name: 'chevron-right',
                  size: 'sm',
                }}
                disabled={isLoading}
                onClick={submitHandler}
                type="button"
              />
            </div>
          </div>
        </div>
      </form>
      <div className="register-form__messages">
        <span className="login-form__message typography-meta">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            className="blue-link"
            tabIndex="0"
            target="_blank"
            href="http://www.elsevier.com/privacypolicy?dgcid=RN_AGCM_Sourced_300005028"
            rel="noreferrer">
            Privacy Policy
          </a>
        </span>
        <span className="login-form__message typography-meta">
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <a
            className="blue-link"
            tabIndex="0"
            target="_blank"
            href="http://www.elsevier.com/termsconditions?dgcid=RN_AGCM_Sourced_300005028"
            rel="noreferrer">
            Terms and Conditions
          </a>
        </span>
      </div>
    </>
  );
}

RegisterFormContent.propTypes = {
  data: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userName: PropTypes.string,
    email: PropTypes.string,
    password: PropTypes.string,
    repeatPassword: PropTypes.string,
    rememberMe: PropTypes.bool,
    licenseAgreement: PropTypes.bool,
    marketingInfo: PropTypes.bool,
  }),
  registerFail: PropTypes.string,
  isShibboleth: PropTypes.bool,
  setData: PropTypes.func,
  isLoading: PropTypes.bool,
  submit: PropTypes.func.isRequired,
  isUsernameVisible: PropTypes.bool,
};

export default RegisterFormContent;
